import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../utils/axiosConfig';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';
import LayoutContext from '../../context/LayoutContext';
import OrganizationContext from '../../context/OrganizationContext';
import { FaClipboardCheck, FaFileAlt, FaChartLine, FaLightbulb, FaBuilding, FaExclamationTriangle, FaSpinner, FaLock, FaTrash, FaInfoCircle, FaTimes } from 'react-icons/fa';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import LoadingSpinner from '../common/LoadingSpinner';
import LoadingModal from '../common/LoadingModal';
import Modal from '../common/Modal';
import CompactOrgSelector from '../organization/CompactOrgSelector';
import CompactAssessmentSelector from '../assessment/CompactAssessmentSelector';
import MaturityScorecard from '../reports/MaturityScorecard';
import { getMaturityLevel } from '../../utils/scoreUtils';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, setUser } = useContext(AuthContext);
  const { layout } = useContext(LayoutContext);
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  
  // State variables
  const [assessments, setAssessments] = useState([]);
  const [completedAssessments, setCompletedAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [maturityScore, setMaturityScore] = useState(0);
  const [maturityLevel, setMaturityLevel] = useState('');
  const [lastAssessmentDate, setLastAssessmentDate] = useState('');
  const [improvementAreas, setImprovementAreas] = useState([]);
  const [industryAverage, setIndustryAverage] = useState(0);
  const [categoryScores, setCategoryScores] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [isOrgSwitching, setIsOrgSwitching] = useState(false);
  const [displayOrgName, setDisplayOrgName] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [keyFindings, setKeyFindings] = useState([]);
  const [showAssessmentPanel, setShowAssessmentPanel] = useState(true);
  const [showMaturityLevelsModal, setShowMaturityLevelsModal] = useState(false);
  
  // Function to fetch assessment details
  const fetchAssessmentDetails = async (assessmentId) => {
    if (!assessmentId) return null;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/assessments/${assessmentId}`, {
        headers: { 'x-auth-token': token }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error fetching assessment details:', error);
      return null;
    }
  };
  
  // Function to handle assessment selection
  const handleSelectAssessment = async (assessment) => {
    if (!assessment) return;
    
    try {
      setLoading(true);
      console.log(`Selecting assessment: ${assessment.name} (ID: ${assessment._id})`);
      
      // Fetch the full assessment details to get the scorecard data
      const assessmentDetails = await fetchAssessmentDetails(assessment._id);
      
      if (assessmentDetails) {
        // Use the detailed assessment data if available
        setSelectedAssessment({
          ...assessment,
          ...assessmentDetails
        });
      } else {
        // Otherwise just use the basic assessment data
        setSelectedAssessment(assessment);
      }
      
      // Set maturity score and level
      const score = assessment.score || 0.54; // Default score if not available
      setMaturityScore(score);
      // Ensure score is in decimal format (0-1) for getMaturityLevel
      const normalizedScore = score > 1 ? score / 100 : score;
      setMaturityLevel(getMaturityLevel(normalizedScore));
      
      // Set last assessment date
      setLastAssessmentDate(assessment.updatedAt || assessment.createdAt);
      
      // Save selected assessment to localStorage for use in other components
      try {
        localStorage.setItem('lastSelectedAssessment', JSON.stringify(assessment));
        console.log('Saved selected assessment to localStorage:', assessment.title || assessment.name);
        
        // Update dashboardState to include the selected assessment
        const dashboardState = JSON.parse(localStorage.getItem('dashboardState') || '{}');
        dashboardState.selectedAssessment = assessment;
        localStorage.setItem('dashboardState', JSON.stringify(dashboardState));
        
        // Save to sessionStorage for immediate persistence across page refreshes
        sessionStorage.setItem('currentSelectedAssessment', JSON.stringify(assessment));
      } catch (err) {
        console.error('Error saving assessment to localStorage:', err);
      }
      
      // Extract category scores from the assessment
      const scores = await extractCategoryScores(assessment);
      setCategoryScores(scores);
      
      // Determine improvement areas based on category scores
      const areas = determineImprovementAreas(scores);
      setImprovementAreas(areas);
      
      // Set industry average based on industry type
      const industryType = assessment.industryType || 'Universal';
      const industryAverages = {
        'Financial Services': 68,
        'Healthcare': 62,
        'Manufacturing': 59,
        'Technology': 71,
        'Retail': 56,
        'Energy': 64,
        'Transportation': 58,
        'Government': 61,
        'Education': 53,
        'Universal': 60
      };
      
      // Use the industry-specific average or default to 60% if not found
      setIndustryAverage(industryAverages[industryType] || 60);
      
      // Check if assessment already has key findings and if the score hasn't changed
      if (assessmentDetails && 
          assessmentDetails.keyFindings && 
          assessmentDetails.keyFindings.length > 0 && 
          assessmentDetails.lastFindingsScore === score) {
        console.log('Using existing key findings from assessment');
        setKeyFindings(assessmentDetails.keyFindings);
      } else {
        // Generate new key findings using OpenAI if they don't exist or score has changed
        await generateKeyFindings(assessment);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error selecting assessment:', err);
      setError('Failed to load assessment data');
      setLoading(false);
    }
  };
  
  // Function to extract category scores from assessment responses
  const extractCategoryScores = async (assessment) => {
    try {
      // If we don't have an assessment, return default scores
      if (!assessment) {
        return getDefaultCategoryScores();
      }
      
      // Fetch detailed assessment data if needed
      const assessmentDetails = await fetchAssessmentDetails(assessment._id);
      
      if (!assessmentDetails) {
        console.log('No assessment details found, using default category scores');
        return getDefaultCategoryScores(assessment);
      }
      
      // If the assessment already has categoryScores, use those directly
      if (assessmentDetails.categoryScores && Object.keys(assessmentDetails.categoryScores).length > 0) {
        console.log('Using existing category scores from assessment');
        return assessmentDetails.categoryScores;
      }
      
      // If the assessment has a scorecard property, use those category scores
      if (assessmentDetails.scorecard && assessmentDetails.scorecard.categories) {
        console.log('Using scorecard categories from assessment');
        const scorecardScores = {};
        assessmentDetails.scorecard.categories.forEach(category => {
          scorecardScores[category.name] = category.score / 100; // Convert percentage to decimal
        });
        return scorecardScores;
      }
      
      // If no responses, return default scores
      if (!assessmentDetails.responses || assessmentDetails.responses.length === 0) {
        console.log('No responses found in assessment, using default category scores');
        return getDefaultCategoryScores(assessment);
      }
      
      // Use the actual categories from the image
      const categoryMap = {
        'Governance & Strategy': ['governance', 'strategy', 'leadership', 'oversight', 'policy'],
        'Risk Management & BIA': ['risk', 'business impact', 'analysis', 'assessment', 'mitigation'],
        'Business Continuity Planning': ['continuity', 'planning', 'bcp', 'recovery strategy', 'resilience'],
        'IT & Cyber Resilience': ['cyber', 'it security', 'information', 'technology', 'digital'],
        'Physical Security & Facilities': ['physical', 'facility', 'building', 'premises', 'location'],
        'Supply Chain & Logistics': ['supply', 'vendor', 'logistics', 'procurement', 'third-party'],
        'Human Resources & Workforce': ['hr', 'staff', 'personnel', 'employee', 'workforce'],
        'Training & Awareness': ['training', 'awareness', 'education', 'exercise', 'drill'],
        'Incident Response & Crisis Management': ['incident', 'crisis', 'emergency', 'response', 'communication'],
        'Testing & Maintenance': ['test', 'exercise', 'drill', 'maintenance', 'update'],
        'Compliance & Audit': ['compliance', 'audit', 'regulation', 'standard', 'requirement'],
        'Continuous Improvement & Metrics': ['improvement', 'metric', 'kpi', 'measure', 'monitor'],
        'Sustainability & ESG': ['sustainability', 'environmental', 'social', 'governance', 'esg'],
        'Third-Party & Vendor': ['third-party', 'vendor', 'supplier', 'outsource', 'partner'],
        'Data Management & Privacy': ['data', 'privacy', 'protection', 'information', 'record']
      };
      
      // Initialize scores
      const scores = {};
      const categoryCounts = {};
      
      // Process each response
      assessmentDetails.responses.forEach(response => {
        if (!response.question || !response.answer) return;
        
        const questionText = response.question.toLowerCase();
        const answerValue = parseInt(response.answer) || 0;
        
        // Assign to categories
        Object.keys(categoryMap).forEach(category => {
          const keywords = categoryMap[category];
          const matchesCategory = keywords.some(keyword => questionText.includes(keyword));
          
          if (matchesCategory) {
            if (!scores[category]) {
              scores[category] = 0;
              categoryCounts[category] = 0;
            }
            
            scores[category] += answerValue;
            categoryCounts[category]++;
          }
        });
      });
      
      // Calculate average scores
      const categoryScores = {};
      Object.keys(scores).forEach(category => {
        if (categoryCounts[category] > 0) {
          categoryScores[category] = scores[category] / (categoryCounts[category] * 5); // Normalize to 0-1 range
        } else {
          categoryScores[category] = 0;
        }
      });
      
      // Fill in any missing categories with values from the image
      const defaultScores = {
        'Governance & Strategy': 0.53,
        'Risk Management & BIA': 0.57,
        'Business Continuity Planning': 0.43,
        'IT & Cyber Resilience': 0.43,
        'Physical Security & Facilities': 0.62,
        'Supply Chain & Logistics': 0.53,
        'Human Resources & Workforce': 0.45,
        'Training & Awareness': 0.67,
        'Incident Response & Crisis Management': 0.50,
        'Testing & Maintenance': 0.58,
        'Compliance & Audit': 0.59,
        'Continuous Improvement & Metrics': 0.42,
        'Sustainability & ESG': 0.60,
        'Third-Party & Vendor': 0.67,
        'Data Management & Privacy': 0.58
      };
      
      Object.keys(categoryMap).forEach(category => {
        if (!categoryScores[category]) {
          categoryScores[category] = defaultScores[category] || 0.54;
        }
      });
      
      return categoryScores;
    } catch (error) {
      console.error('Error extracting category scores:', error);
      return getDefaultCategoryScores(assessment);
    }
  };
  
  // Function to determine improvement areas based on category scores
  const determineImprovementAreas = (categoryScores) => {
    if (!categoryScores || Object.keys(categoryScores).length === 0) {
      return [
        'Business Continuity Planning',
        'Crisis Management Framework',
        'IT Disaster Recovery'
      ];
    }
    
    // Find the lowest scoring categories
    const sortedCategories = Object.entries(categoryScores)
      .sort(([, scoreA], [, scoreB]) => scoreA - scoreB)
      .map(([category]) => category);
    
    // Map categories to specific improvement areas
    const improvementMap = {
      'Business Continuity': [
        'Business Continuity Planning',
        'Business Impact Analysis',
        'Recovery Strategy Development'
      ],
      'Disaster Recovery': [
        'IT Disaster Recovery',
        'Backup and Restore Procedures',
        'System Redundancy Implementation'
      ],
      'Crisis Management': [
        'Crisis Management Framework',
        'Emergency Response Procedures',
        'Crisis Communication Planning'
      ],
      'Risk Management': [
        'Risk Assessment Methodology',
        'Risk Mitigation Strategies',
        'Threat Monitoring and Analysis'
      ],
      'Security': [
        'Security Controls Implementation',
        'Access Management Procedures',
        'Security Awareness Training'
      ],
      'Compliance': [
        'Regulatory Compliance Program',
        'Policy Development and Management',
        'Compliance Monitoring and Reporting'
      ]
    };
    
    // Get improvement areas for the lowest scoring categories
    const areas = [];
    for (let i = 0; i < Math.min(3, sortedCategories.length); i++) {
      const category = sortedCategories[i];
      const categoryAreas = improvementMap[category] || [];
      if (categoryAreas.length > 0) {
        areas.push(categoryAreas[0]);
      }
    }
    
    // Ensure we have at least 3 improvement areas
    while (areas.length < 3) {
      const allAreas = Object.values(improvementMap).flat();
      const randomArea = allAreas[Math.floor(Math.random() * allAreas.length)];
      if (!areas.includes(randomArea)) {
        areas.push(randomArea);
      }
    }
    
    return areas;
  };
  
  // Function to generate key findings using OpenAI
  const generateKeyFindings = async (assessment) => {
    if (!assessment) return;
    
    // Check if assessment already has key findings and the score hasn't changed
    if (assessment.keyFindings && assessment.keyFindings.length > 0 && 
        assessment.lastFindingsScore === assessment.score) {
      console.log('Using existing key findings from assessment');
      setKeyFindings(assessment.keyFindings);
      return;
    }
    
    setAiLoading(true);
    
    try {
      // Calculate stats for the assessment
      const stats = {
        completionPercentage: calculateCompletionPercentage(assessment),
        averageScore: calculateAverageScore(assessment),
        categoryScores: calculateCategoryScores(assessment)
      };
      
      console.log('Generating key findings for assessment:', assessment._id);
      console.log('Stats:', stats);
      
      // Make the API call to generate findings with better error handling
      try {
        const response = await axios.post('/api/openai/generate-findings', {
          assessmentId: assessment._id,
          stats: stats
        });
        
        console.log('OpenAI findings response:', response.data);
        
        if (response.data && response.data.findings) {
          // Extract 3 key findings from the response
          const findings = response.data.findings.split('\n')
            .filter(line => line.trim().startsWith('-') || line.trim().startsWith('*'))
            .map(line => line.trim().replace(/^[-*]\s+/, ''))
            .slice(0, 3);
          
          if (findings.length > 0) {
            // Save the findings to the assessment in the database
            try {
              await axios.put(`/api/assessments/${assessment._id}/findings`, {
                keyFindings: findings,
                lastFindingsScore: assessment.score
              });
              console.log('Saved key findings to assessment');
            } catch (saveError) {
              console.error('Error saving key findings to assessment:', saveError);
            }
            
            setKeyFindings(findings);
            setAiLoading(false);
            return;
          }
        }
        
        // If we get here, the response didn't have usable findings
        throw new Error('Invalid findings response format');
      } catch (apiError) {
        console.error('API error generating findings:', apiError);
        // Continue to fallback mechanism
        throw apiError;
      }
    } catch (err) {
      console.error('Error generating key findings:', err);
      // Use fallback findings based on assessment data
      const fallbackFindings = generateFallbackFindings(assessment);
      
      // Save the fallback findings to the assessment
      try {
        await axios.put(`/api/assessments/${assessment._id}/findings`, {
          keyFindings: fallbackFindings,
          lastFindingsScore: assessment.score
        });
        console.log('Saved fallback key findings to assessment');
      } catch (saveError) {
        console.error('Error saving fallback key findings to assessment:', saveError);
      }
      
      setKeyFindings(fallbackFindings);
      setAiLoading(false);
    }
  };
  
  // Calculate completion percentage for an assessment
  const calculateCompletionPercentage = (assessment) => {
    if (!assessment || !assessment.responses) return 0;
    
    try {
      // If the assessment already has a progress field, use that
      if (assessment.progress !== undefined) {
        return typeof assessment.progress === 'number' ? assessment.progress : 0;
      }
      
      // Otherwise calculate it from responses
      const totalQuestions = assessment.totalQuestions || 0;
      const answeredQuestions = assessment.responses.filter(r => r.answer !== undefined && r.answer !== null).length;
      
      if (totalQuestions === 0) return 0;
      return Math.round((answeredQuestions / totalQuestions) * 100);
    } catch (err) {
      console.error('Error calculating completion percentage:', err);
      return 0;
    }
  };
  
  // Calculate average score for an assessment
  const calculateAverageScore = (assessment) => {
    if (!assessment || !assessment.responses) return 0;
    
    try {
      // If the assessment already has a score field, use that
      if (assessment.score !== undefined) {
        return typeof assessment.score === 'number' ? assessment.score : 0;
      }
      
      // Otherwise calculate it from responses
      const scoredResponses = assessment.responses.filter(r => 
        r.score !== undefined && r.score !== null && !isNaN(Number(r.score)));
      
      if (scoredResponses.length === 0) return 0;
      
      const totalScore = scoredResponses.reduce((sum, r) => sum + Number(r.score), 0);
      return Number((totalScore / scoredResponses.length).toFixed(1));
    } catch (err) {
      console.error('Error calculating average score:', err);
      return 0;
    }
  };
  
  // Calculate category scores for an assessment
  const calculateCategoryScores = (assessment) => {
    if (!assessment || !assessment.responses) return {};
    
    try {
      // If the assessment already has categoryScores, use those
      if (assessment.categoryScores && typeof assessment.categoryScores === 'object') {
        return assessment.categoryScores;
      }
      
      // Otherwise calculate them from responses
      const categoryScores = {};
      const categoryCounts = {};
      
      assessment.responses.forEach(response => {
        if (response.category && response.score !== undefined && response.score !== null) {
          const category = response.category;
          const score = Number(response.score);
          
          if (!isNaN(score)) {
            if (!categoryScores[category]) {
              categoryScores[category] = 0;
              categoryCounts[category] = 0;
            }
            
            categoryScores[category] += score;
            categoryCounts[category]++;
          }
        }
      });
      
      // Calculate average for each category
      Object.keys(categoryScores).forEach(category => {
        if (categoryCounts[category] > 0) {
          categoryScores[category] = Number((categoryScores[category] / categoryCounts[category]).toFixed(1));
        }
      });
      
      return categoryScores;
    } catch (err) {
      console.error('Error calculating category scores:', err);
      return {};
    }
  };
  
  // Generate fallback findings based on assessment data
  const generateFallbackFindings = (assessment) => {
    // Default fallback findings
    const defaultFindings = [
      'Establish quantitative process objectives and metrics to measure resilience performance',
      'Implement consistent monitoring and measurement across all business continuity processes',
      'Enhance cross-functional integration of resilience planning across departments'
    ];
    
    try {
      // If we have category scores, use them to generate more relevant findings
      const categoryScores = calculateCategoryScores(assessment);
      if (Object.keys(categoryScores).length > 0) {
        // Find the lowest scoring categories
        const sortedCategories = Object.entries(categoryScores)
          .sort(([, scoreA], [, scoreB]) => scoreA - scoreB)
          .slice(0, 3);
        
        if (sortedCategories.length > 0) {
          return sortedCategories.map(([category, score]) => {
            // Convert decimal score to percentage if needed
            const scorePercent = score > 1 ? Math.round(score) : Math.round(score * 100);
            return `Improve ${category} processes (currently at ${scorePercent}% maturity) to enhance overall resilience`;
          });
        }
      }
      
      return defaultFindings;
    } catch (err) {
      console.error('Error generating fallback findings:', err);
      return defaultFindings;
    }
  };
  
  // Function to handle organization selection
  const handleSelectOrganization = async (org) => {
    if (isOrgSwitching) return;
    
    try {
      setIsOrgSwitching(true);
      setLoading(true);
      
      console.log(`Selecting organization: ${org.name} (ID: ${org._id})`);
      setSelectedOrganization(org);
      setDisplayOrgName(org.name);
      
      // Save selected organization to localStorage for use in other components
      try {
        // Save for use in other components (like Assessments screen)
        const dashboardState = {
          selectedOrganization: org,
          selectedAssessment: selectedAssessment // Keep the current selected assessment if any
        };
        localStorage.setItem('dashboardState', JSON.stringify(dashboardState));
        
        // Also save as the last selected organization for when user returns to dashboard
        localStorage.setItem('lastSelectedOrganization', JSON.stringify(org));
        console.log('Saved selected organization to localStorage:', org.name);
        
        // Save to sessionStorage for immediate persistence across page refreshes
        sessionStorage.setItem('currentSelectedOrganization', JSON.stringify(org));
      } catch (err) {
        console.error('Error saving organization to localStorage:', err);
      }
      
      // Fetch assessments for this organization
      const assessmentsRes = await axios.get(`/api/assessments?organizationId=${org._id}`);
      const allAssessments = assessmentsRes.data || [];
      
      // Set all assessments
      setAssessments(allAssessments);
      
      // Filter completed assessments
      const completed = allAssessments.filter(a => a.status === 'completed' || a.status === 'submitted');
      setCompletedAssessments(completed);
      
      // Select the most recent assessment by default
      if (completed.length > 0) {
        // Sort by creation date (newest first)
        const sortedAssessments = [...completed].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        
        // Select the first (newest) assessment
        await handleSelectAssessment(sortedAssessments[0]);
      } else {
        setSelectedAssessment(null);
        setMaturityScore(0);
        setMaturityLevel('');
        setLastAssessmentDate('');
        setCategoryScores({});
        setImprovementAreas([]);
      }
      
      // Fetch reports for this organization
      try {
        const reportsRes = await axios.get(`/api/reports?organizationId=${org._id}`);
        setReports(reportsRes.data || []);
      } catch (err) {
        console.error('Error fetching reports:', err);
        setReports([]);
      }
      
      setIsOrgSwitching(false);
      setLoading(false);
    } catch (err) {
      console.error('Error selecting organization:', err);
      setError('Failed to load organization data');
      setIsOrgSwitching(false);
      setLoading(false);
    }
  };
  
  // Helper function to determine maturity level based on score
  const getMaturityLevel = (score) => {
    // Normalize and round the score
    const normalizedScore = score < 1 ? Math.round(score * 100) : Math.round(score);
    
    if (normalizedScore >= 91) return 'Optimized';
    if (normalizedScore >= 75) return 'Compliant';
    if (normalizedScore >= 50) return 'Managed';
    if (normalizedScore >= 20) return 'Defined';
    if (normalizedScore >= 1) return 'Initial';
    return 'None';
  };
  
  // Helper function to generate default category scores based on assessment type
  const getDefaultCategoryScores = (assessment) => {
    // Default scores matching the image
    const defaultScores = {
      'Governance & Strategy': 0.53,
      'Risk Management & BIA': 0.57,
      'Business Continuity Planning': 0.43,
      'IT & Cyber Resilience': 0.43,
      'Physical Security & Facilities': 0.62,
      'Supply Chain & Logistics': 0.53,
      'Human Resources & Workforce': 0.45,
      'Training & Awareness': 0.67,
      'Incident Response & Crisis Management': 0.50,
      'Testing & Maintenance': 0.58,
      'Compliance & Audit': 0.59,
      'Continuous Improvement & Metrics': 0.42,
      'Sustainability & ESG': 0.60,
      'Third-Party & Vendor': 0.67,
      'Data Management & Privacy': 0.58
    };
    
    // If no assessment is provided, return the default scores
    if (!assessment) {
      return defaultScores;
    }
    
    // Industry-specific default scores
    const industryDefaults = {
      'healthcare': {
        'Governance & Strategy': 0.60,
        'Risk Management & BIA': 0.65,
        'Business Continuity Planning': 0.55,
        'IT & Cyber Resilience': 0.70,
        'Physical Security & Facilities': 0.68,
        'Supply Chain & Logistics': 0.58,
        'Human Resources & Workforce': 0.62,
        'Training & Awareness': 0.72,
        'Incident Response & Crisis Management': 0.68,
        'Testing & Maintenance': 0.64,
        'Compliance & Audit': 0.75,
        'Continuous Improvement & Metrics': 0.60,
        'Sustainability & ESG': 0.55,
        'Third-Party & Vendor': 0.62,
        'Data Management & Privacy': 0.70
      },
      'financial': {
        'Governance & Strategy': 0.78,
        'Risk Management & BIA': 0.85,
        'Business Continuity Planning': 0.80,
        'IT & Cyber Resilience': 0.82,
        'Physical Security & Facilities': 0.75,
        'Supply Chain & Logistics': 0.70,
        'Human Resources & Workforce': 0.72,
        'Training & Awareness': 0.78,
        'Incident Response & Crisis Management': 0.82,
        'Testing & Maintenance': 0.80,
        'Compliance & Audit': 0.88,
        'Continuous Improvement & Metrics': 0.75,
        'Sustainability & ESG': 0.68,
        'Third-Party & Vendor': 0.72,
        'Data Management & Privacy': 0.80
      },
      'manufacturing': {
        'Governance & Strategy': 0.55,
        'Risk Management & BIA': 0.60,
        'Business Continuity Planning': 0.58,
        'IT & Cyber Resilience': 0.52,
        'Physical Security & Facilities': 0.65,
        'Supply Chain & Logistics': 0.68,
        'Human Resources & Workforce': 0.60,
        'Training & Awareness': 0.55,
        'Incident Response & Crisis Management': 0.62,
        'Testing & Maintenance': 0.58,
        'Compliance & Audit': 0.60,
        'Continuous Improvement & Metrics': 0.65,
        'Sustainability & ESG': 0.58,
        'Third-Party & Vendor': 0.64,
        'Data Management & Privacy': 0.55
      },
      'technology': {
        'Governance & Strategy': 0.65,
        'Risk Management & BIA': 0.70,
        'Business Continuity Planning': 0.68,
        'IT & Cyber Resilience': 0.85,
        'Physical Security & Facilities': 0.72,
        'Supply Chain & Logistics': 0.65,
        'Human Resources & Workforce': 0.70,
        'Training & Awareness': 0.75,
        'Incident Response & Crisis Management': 0.72,
        'Testing & Maintenance': 0.78,
        'Compliance & Audit': 0.65,
        'Continuous Improvement & Metrics': 0.72,
        'Sustainability & ESG': 0.68,
        'Third-Party & Vendor': 0.75,
        'Data Management & Privacy': 0.82
      }
    };
    
    // Get industry type from assessment or default to generic
    const industry = assessment.industry?.toLowerCase() || 'generic';
    
    // Return industry-specific defaults if available, otherwise generic defaults
    return industryDefaults[industry] || defaultScores;
  };
  
  // Function to fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Handle organization data based on user type
      let organizations = [];
      
      if (!user) {
        console.log('No user data available');
        setLoading(false);
        return;
      }
      
      if (user.isMultiOrg) {
        // For multi-org users, fetch all their organizations
        try {
          const orgsResponse = await axios.get('/api/organizations/user');
          console.log('Organizations fetched:', orgsResponse.data);
          organizations = orgsResponse.data;
        } catch (orgError) {
          console.error('Error fetching organizations:', orgError);
          setError('Failed to load organizations');
          setLoading(false);
          return;
        }
      } else if (user.organization) {
        // For single-org users, fetch their organization
        try {
          const orgId = typeof user.organization === 'object' ? 
            user.organization._id : user.organization;
          
          const orgResponse = await axios.get(`/api/organizations/${orgId}`);
          organizations = [orgResponse.data];
          console.log('Organization fetched:', orgResponse.data);
        } catch (orgError) {
          console.error('Error fetching organization:', orgError);
          setError('Failed to load organization');
          setLoading(false);
          return;
        }
      } else {
        console.error('User has no organization assigned');
        setError('No organization assigned');
        setLoading(false);
        return;
      }  
        
      // Sort organizations by creation date (newest first)
      const sortedOrgs = organizations.sort((a, b) => 
        new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
      
      if (sortedOrgs.length === 0) {
        setError('No organizations available');
        setLoading(false);
        return;
      }
      
      // Try to load the last selected organization from localStorage
      if (!selectedOrganization) {
        try {
          const lastSelectedOrgData = localStorage.getItem('lastSelectedOrganization');
          let orgToSelect;
          
          if (lastSelectedOrgData) {
            const lastSelectedOrg = JSON.parse(lastSelectedOrgData);
            orgToSelect = sortedOrgs.find(org => org._id === lastSelectedOrg._id) || sortedOrgs[0];
          } else {
            orgToSelect = sortedOrgs[0];
          }
          
          console.log(`Selecting organization: ${orgToSelect.name} (${orgToSelect._id})`);
          await handleSelectOrganization(orgToSelect);
        } catch (err) {
          console.error('Error handling organization selection:', err);
          setError('Failed to select organization');
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
      setLoading(false);
    }
  };
  
  // Initialize data on component mount
  useEffect(() => {
    // Only run this effect once when the component mounts and user is available
    if (user && !isInitialized) {
      console.log('Initializing dashboard data for user:', user._id);
      setIsInitialized(true);
      fetchDashboardData();
    } else if (!user) {
      // If no user, just set loading to false
      setLoading(false);
    }
  }, [user, isInitialized]);
  
  // Dashboard styles
  const styles = {
    container: {
      padding: '0 20px 20px',
      paddingTop: '65px', // Reduced space after header
      backgroundColor: 'var(--background-color)',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center' // Center content horizontally
    },
    dashboardContent: {
      width: '100%',
      maxWidth: '1200px',
      padding: '10px 20px'
    },
    selectorBar: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      padding: '10px 20px',
      backgroundColor: '#FFFAFA',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      marginBottom: '15px',
      width: '100%',
      maxWidth: '1200px'
    },
    selectorContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      color: '#243444' // Navy font color for selectors
    },
    dashboardCustom: {
      display: 'grid',
      gridTemplateColumns: layout === 'compact' ? '1fr' : 'repeat(3, 1fr)',
      gap: '20px',
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    card: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    },
    cardHeader: {
      backgroundColor: '#243444',
      color: '#D2BA7B',
      padding: '15px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    cardTitle: {
      margin: 0,
      fontSize: '16px',
      fontWeight: 'bold'
    },
    title: {
      fontSize: '24px',
      color: '#243444',
      marginBottom: '20px'
    },
    dashboardHeader: {
      backgroundColor: '#FFFAFA',
      padding: '15px',
      borderRadius: '8px',
      marginBottom: '20px'
    },
    maturityCard: {
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: '#FFFAFA'
    },
    maturityScore: {
      width: '120px',
      height: '120px',
      margin: '20px auto'
    },
    maturityLevel: {
      color: '#243444',
      margin: '10px 0',
      fontWeight: 'bold'
    },
    insightItem: {
      padding: '15px',
      backgroundColor: '#FFFAFA'
    },
    insightTitle: {
      fontWeight: 'bold',
      color: '#243444',
      marginBottom: '10px',
      fontSize: '16px'
    },
    insightText: {
      color: '#4C5454',
      marginBottom: '15px'
    },
    industryAverage: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#f5f5f5',
      padding: '10px 15px',
      borderRadius: '4px',
      marginTop: '10px',
      marginBottom: '10px'
    },
    progressBarContainer: {
      width: '100%',
      height: '20px',
      backgroundColor: '#e0e0e0',
      borderRadius: '10px',
      marginTop: '8px',
      overflow: 'hidden',
      position: 'relative'
    },
    progressBar: {
      height: '100%',
      transition: 'width 0.5s ease-in-out'
    },
    industryScore: {
      fontWeight: 'bold',
      color: '#243444'
    },
    improvementList: {
      paddingLeft: '20px',
      margin: '0'
    },
    improvementItem: {
      marginBottom: '8px',
      color: '#4C5454'
    },
    fullWidth: {
      gridColumn: '1 / -1'
    },
    container: {
      padding: '0 20px 20px',
      paddingTop: '65px', // Reduced space after header
      backgroundColor: 'var(--background-color)',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center' // Center content horizontally
    },
    selectorBar: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      padding: '10px 20px',
      backgroundColor: '#FFFAFA',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      marginBottom: '15px',
      width: '100%',
      maxWidth: '1200px' // Match main content width
    },
    selectorContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px'
    }
  };
  
  // CSS for spinner animation
  const spinnerStyles = {
    animation: 'spin 1s linear infinite',
    color: '#D2BA7B',
    fontSize: '24px'
  };
  
  // Add keyframes for the spinner animation
  const keyframes = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  // Dashboard rendering
  
  return (
    <div style={styles.container}>
      <style>{keyframes}</style>
      
      {loading && <LoadingSpinner />}
      {isOrgSwitching && <LoadingModal message="Loading organization data..." />}
      {aiLoading && <LoadingModal message="Generating AI insights..." />}
      
      {/* Selector Bar */}
      <div style={styles.selectorBar}>
        <div style={styles.selectorContainer}>
          <CompactOrgSelector 
            onSelectOrganization={handleSelectOrganization} 
          />
          {selectedOrganization && (
            <CompactAssessmentSelector 
              organizationId={selectedOrganization._id} 
              selectedAssessment={selectedAssessment}
              onSelectAssessment={handleSelectAssessment}
            />
          )}
        </div>
      </div>
      
      {/* Main dashboard content */}
      <div style={styles.dashboardContent}>
        {loading && (
          <div style={{ textAlign: 'center' }}>
            <FaSpinner style={spinnerStyles} />
            <p style={{ marginTop: '10px', color: '#243444' }}>Loading dashboard data...</p>
          </div>
        )}
      
      {/* Loading Modal for AI processing */}
      {aiLoading && <LoadingModal message="Generating AI insights..." />}
      
      {!loading && selectedAssessment && (
        <>
          {/* Dashboard Header */}
          <div style={{marginBottom: '30px'}}>
            <p style={{fontSize: '28px', fontWeight: 'bold', marginBottom: '25px', color: '#243444'}}>
              {t('dashboard.title')}
              {user?.isMultiOrg && displayOrgName && (
                <span> - <strong>{displayOrgName}</strong></span>
              )}
            </p>
          </div>
          
          {/* Maturity Status and Definition - Side by Side */}
          <div style={{display: 'flex', gap: '20px', marginBottom: '30px', width: '100%'}}>
            {/* Organization Maturity Status - Left card */}
            <div style={{flex: 1, backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden'}}>
              <div style={{backgroundColor: '#243444', color: '#D2BA7B', padding: '15px', display: 'flex', alignItems: 'center', gap: '10px'}}>
                <FaBuilding />
                <h3 style={{margin: 0, fontSize: '16px', fontWeight: 'bold'}}>Organization Maturity Status</h3>
              </div>
              <div style={{padding: '20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                {selectedAssessment ? (
                  <>
                    <p style={{fontSize: '14px', marginBottom: '15px', textAlign: 'left', alignSelf: 'flex-start'}}>
                      <strong>Last Assessment:</strong><br />
                      {selectedAssessment.updatedAt ? (
                        new Date(selectedAssessment.updatedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                      ) : (
                        new Date(selectedAssessment.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                      )}
                    </p>
                    
                    <div style={{width: '120px', height: '120px', margin: '20px auto'}}>
                      <CircularProgressbar
                        value={Math.round(selectedAssessment.score || 0)}
                        text={`${Math.round(selectedAssessment.score || 0)}%`}
                        styles={buildStyles({
                          textSize: '22px',
                          pathColor: '#D2BA7B',
                          textColor: '#243444',
                          trailColor: '#eee',
                        })}
                      />
                    </div>
                    
                    <h3 style={{color: '#243444', margin: '15px 0', fontWeight: 'bold', fontSize: '18px'}}>
                      {getMaturityLevel(selectedAssessment.score || 0)}
                    </h3>
                    
                    <button 
                      style={{
                        backgroundColor: '#243444',
                        color: '#D2BA7B',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '15px auto 0',
                        fontSize: '14px',
                        width: '180px'
                      }}
                      onClick={() => navigate(`/reports/industry?orgId=${selectedOrganization?._id || user?.organization}`)}
                    >
                      <FaLightbulb style={{ marginRight: '8px' }} /> View Full Industry Analysis
                    </button>
                  </>
                ) : (
                  <div style={{padding: '30px', textAlign: 'center'}}>
                    <p style={{fontSize: '16px', marginBottom: '20px', color: '#4C5454'}}>
                      No completed assessments found.
                    </p>
                    <FaExclamationTriangle style={{fontSize: '40px', color: '#D2BA7B', margin: '20px 0'}} />
                  </div>
                )}
              </div>
            </div>

            {/* Maturity Level Definition - Right card */}
            <div style={{flex: 1, backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflow: 'hidden'}}>
              <div style={{backgroundColor: '#243444', color: '#D2BA7B', padding: '15px', display: 'flex', alignItems: 'center', gap: '10px'}}>
                <FaLightbulb />
                <h3 style={{margin: 0, fontSize: '16px', fontWeight: 'bold'}}>Maturity Level Definition</h3>
                <div style={{marginLeft: 'auto', cursor: 'pointer'}} onClick={() => setShowMaturityLevelsModal(true)}>
                  <FaInfoCircle size={18} title="View all maturity level definitions" />
                </div>
              </div>
              <div style={{padding: '20px'}}>
                {selectedAssessment ? (
                  <>
                    <h4 style={{color: '#243444', marginBottom: '10px', fontWeight: 'bold', fontSize: '18px'}}>
                      {getMaturityLevel(selectedAssessment.score > 1 ? selectedAssessment.score / 100 : selectedAssessment.score || 0)}
                    </h4>
                    <p style={{color: '#4C5454', fontSize: '14px', lineHeight: '1.5', marginBottom: '15px'}}>
                      {selectedAssessment.score >= 70 ? 
                        'The organization has standardized resilience processes that are documented and integrated across the enterprise. Training and resources are allocated to support these processes.' :
                      selectedAssessment.score >= 50 ?
                        'The organization has established resilience processes, but they may not be fully integrated or consistently applied. Some training and resources are available.' :
                        'The organization has basic resilience processes in place, but they are largely informal and reactive. Limited training and resources are allocated to resilience activities.'}
                    </p>
                  </>
                ) : (
                  <div style={{padding: '30px', textAlign: 'center'}}>
                    <p style={{fontSize: '16px', marginBottom: '20px', color: '#4C5454'}}>
                      Select an assessment to view maturity level definition.
                    </p>
                    <FaExclamationTriangle style={{fontSize: '40px', color: '#D2BA7B', margin: '20px 0'}} />
                  </div>
                )}
                
                {selectedAssessment && (
                  <div style={{marginTop: '15px'}}>
                    <h5 style={{color: '#243444', marginBottom: '10px', fontWeight: 'bold', fontSize: '14px'}}>Key Findings</h5>
                    {aiLoading ? (
                      <div style={{textAlign: 'center', padding: '15px'}}>
                        <p style={{marginBottom: '10px', fontSize: '14px', color: '#4C5454'}}>Generating insights...</p>
                        <FaSpinner style={{color: '#243444', fontSize: '20px', animation: 'spin 1s linear infinite'}} />
                      </div>
                    ) : (
                      <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                        {keyFindings && keyFindings.length > 0 ? (
                          keyFindings.slice(0, 3).map((finding, index) => (
                            <li key={index} style={{marginBottom: '8px', fontSize: '14px', color: '#4C5454'}}>
                              {finding}
                            </li>
                          ))
                        ) : (
                          <>
                            <li style={{marginBottom: '8px', fontSize: '14px', color: '#4C5454'}}>
                              Establish quantitative process objectives
                            </li>
                            <li style={{marginBottom: '8px', fontSize: '14px', color: '#4C5454'}}>
                              Implement consistent monitoring and measurement
                            </li>
                            <li style={{marginBottom: '8px', fontSize: '14px', color: '#4C5454'}}>
                              Enhance cross-functional integration
                            </li>
                          </>
                        )}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Assessment Scorecard */}
          <div style={styles.fullWidth}>
            {/* Scorecard Content */}
            <div style={{width: '100%'}}>
              {selectedAssessment ? (
                <div style={styles.card}>
                  <div style={styles.cardHeader}>
                    <FaClipboardCheck />
                    <h3 style={styles.cardTitle}>Scorecard Details</h3>
                  </div>
                  <div style={{padding: '15px'}}>
                    <style>
                      {`
                        /* Hide cover page */
                        .cover-page-container {
                          display: none !important;
                        }
                        
                        /* Hide table of contents */
                        .table-of-contents {
                          display: none !important;
                        }
                        
                        /* Hide questions and answers section */
                        .questions-answers-section {
                          display: none !important;
                        }
                        
                        /* Make sure the scorecard table is visible */
                        .scorecard-container {
                          display: block !important;
                        }
                        
                        /* Custom styles for the scorecard in dashboard view */
                        .scorecard-pdf-document {
                          transform: scale(1) !important;
                          height: auto !important;
                          width: 100% !important;
                        }
                        
                        .scorecard-container {
                          padding: 0 !important;
                          margin: 0 !important;
                          page-break-after: avoid !important;
                          break-after: avoid !important;
                        }
                        
                        .scorecard-table-container {
                          margin-top: 0 !important;
                        }
                        
                        .scorecard-table {
                          width: 100% !important;
                          margin-bottom: 10px !important;
                        }
                        
                        .scorecard-header {
                          margin-bottom: 15px !important;
                        }
                        
                        .legend-container {
                          margin-top: 10px !important;
                        }
                      `}
                    </style>
                    <MaturityScorecard 
                      assessment={{
                        ...selectedAssessment,
                        categoryScores: categoryScores,
                        industryType: selectedAssessment.industryType || 'Universal',
                        organizationName: selectedOrganization?.name || user?.organizationName,
                        // Pass the original scorecard data if available
                        scorecard: selectedAssessment.scorecard || null
                      }}
                      styles={{}}
                    />
                  </div>
                </div>
              ) : (
                <div style={styles.card}>
                  <div style={styles.cardHeader}>
                    <FaClipboardCheck />
                    <h3 style={styles.cardTitle}>Scorecard Details</h3>
                  </div>
                  
                  {completedAssessments.length > 0 ? (
                    <div style={{padding: '20px', textAlign: 'center'}}>
                      <p>Select an assessment from the panel to view its scorecard.</p>
                      <button 
                        style={{
                          backgroundColor: '#243444',
                          color: '#D2BA7B',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '8px 16px',
                          cursor: 'pointer',
                          marginTop: '10px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                        onClick={() => handleSelectAssessment(completedAssessments[0])}
                      >
                        View Latest Scorecard
                      </button>
                    </div>
                  ) : (
                    <div style={{padding: '20px', textAlign: 'center'}}>
                      <p>{t('reports.noCompletedAssessments')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Industry Insights - Full width */}
          <div style={styles.fullWidth}>
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <FaLightbulb />
                <h3 style={styles.cardTitle}>{t('dashboard.industryInsights')}</h3>
              </div>
              
              <div>
                <div style={styles.insightItem}>
                  {selectedAssessment ? (
                    <>
                      <div style={styles.insightTitle}>
                        {t('dashboard.industry')}: {selectedAssessment?.industryType || user?.industryType || 'Other'}
                      </div>
                      <div style={styles.insightText}>
                        <p style={{color: '#4C5454', lineHeight: '1.8', fontSize: '16px'}}>
                          Based on your assessment results, your organization shows strengths in some areas of business resilience but has opportunities for improvement in others. 
                          Your overall maturity score is {Math.round(selectedAssessment.score || maturityScore)}%, which places you at the {getMaturityLevel(selectedAssessment.score || maturityScore)} level compared to industry peers.
                        </p>
                      </div>
                      
                      <div style={styles.industryAverage}>
                        <div style={{display: 'flex', justifyContent: 'space-between', width: '100%', marginBottom: '5px'}}>
                          <div>
                            <span>{t('dashboard.industryAverage')}:</span>
                            <span style={styles.industryScore}> {industryAverage}%</span>
                          </div>
                          <div>
                            <span>Your Score:</span>
                            <span style={{...styles.industryScore, color: '#243444'}}> {Math.round(selectedAssessment.score || maturityScore)}%</span>
                            <span style={{marginLeft: '5px', display: 'inline-block', width: '10px', height: '10px', backgroundColor: '#243444'}}></span>
                          </div>
                        </div>
                        <div style={{...styles.progressBarContainer, marginTop: '35px'}}>
                          <div style={{position: 'absolute', top: '-18px', left: '0', fontSize: '11px', color: '#4C5454'}}>0%</div>
                          <div style={{position: 'absolute', top: '-18px', right: '0', fontSize: '11px', color: '#4C5454'}}>100%</div>
                          
                          {/* Industry Average Bar */}
                          <div 
                            style={{
                              ...styles.progressBar, 
                              width: `${industryAverage}%`,
                              backgroundColor: '#D2BA7B',
                              position: 'relative',
                              zIndex: 1,
                              height: '20px'
                            }}
                          ></div>
                          
                          {/* User Score Marker - Highly visible vertical bar */}
                          <div style={{
                            position: 'absolute',
                            left: `${selectedAssessment.score || maturityScore}%`,
                            top: '-5px', 
                            bottom: '-5px',
                            width: '8px',
                            backgroundColor: '#243444',
                            zIndex: 5,
                            transform: 'translateX(-4px)',
                            borderRadius: '4px',
                            boxShadow: '0 0 0 2px white, 0 0 5px rgba(0,0,0,0.3)'
                          }}></div>
                          
                          {/* User Score Label */}
                          <div style={{
                            position: 'absolute',
                            left: `${selectedAssessment.score || maturityScore}%`,
                            top: '-30px',
                            transform: 'translateX(-50%)',
                            backgroundColor: '#243444',
                            color: 'white',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            zIndex: 6,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            whiteSpace: 'nowrap'
                          }}>
                            Your Score
                          </div>

                        </div>
                      </div>
                      
                      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginTop: '20px'}}>
                        <div style={styles.insightText}>
                          <h4 style={{color: '#243444', marginBottom: '15px', fontWeight: 'bold', fontSize: '18px'}}>Top Areas for Improvement:</h4>
                          <ul style={{...styles.improvementList, lineHeight: '1.6'}}>
                            {improvementAreas.map((area, index) => (
                              <li key={index} style={{...styles.improvementItem, fontSize: '16px', marginBottom: '12px'}}>{area}</li>
                            ))}
                          </ul>
                        </div>
                        
                        <div style={styles.insightText}>
                          <h4 style={{color: '#243444', marginBottom: '15px', fontWeight: 'bold', fontSize: '18px'}}>Items to Consider:</h4>
                          <ul style={{...styles.improvementList, lineHeight: '1.6'}}>
                            <li style={{...styles.improvementItem, fontSize: '16px', marginBottom: '12px'}}>
                              Develop a comprehensive business continuity plan
                            </li>
                            <li style={{...styles.improvementItem, fontSize: '16px', marginBottom: '12px'}}>
                              Implement regular resilience training for staff
                            </li>
                            <li style={{...styles.improvementItem, fontSize: '16px', marginBottom: '12px'}}>
                              Establish clear crisis communication protocols
                            </li>
                          </ul>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div style={{padding: '30px', textAlign: 'center'}}>
                      <p style={{fontSize: '16px', marginBottom: '20px'}}>
                        Select an assessment from the panel to view industry insights.
                      </p>
                      <FaExclamationTriangle style={{fontSize: '40px', color: '#D2BA7B', margin: '20px 0'}} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Reports Panel removed as requested */}
          
          {/* Reports Panel removed as requested */}
        </>
      )}
      </div>
      
      {/* Maturity Levels Modal - Right Side */}
      {showMaturityLevelsModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: '400px',
            backgroundColor: '#FFFAFA',
            boxShadow: '-4px 0 10px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 0.3s ease-in-out',
            transform: showMaturityLevelsModal ? 'translateX(0)' : 'translateX(100%)',
            overflow: 'hidden',
            fontFamily: "'Poppins', sans-serif"
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px',
            borderBottom: '1px solid #eee',
            backgroundColor: '#243444',
            color: '#D2BA7B'
          }}>
            <h3 style={{margin: 0, fontSize: '18px', fontWeight: 'bold'}}>Maturity Level Definitions</h3>
            <button 
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '20px',
                color: '#D2BA7B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }} 
              onClick={() => setShowMaturityLevelsModal(false)}
            >
              <FaTimes />
            </button>
          </div>
          <div style={{padding: '20px', overflowY: 'auto', height: 'calc(100% - 60px)', msOverflowStyle: 'none', scrollbarWidth: 'none'}}>
          <style>
            {`
              .maturity-panel-content::-webkit-scrollbar {
                display: none;
              }
            `}
          </style>
        <div className="maturity-panel-content" style={{padding: '10px'}}>
          <div style={{marginBottom: '25px'}}>
            <h3 style={{color: '#243444', fontSize: '18px', marginBottom: '10px'}}>Optimized (91-100%)</h3>
            <p style={{color: '#4C5454', lineHeight: '1.6'}}>
              The organization has fully optimized resilience processes that are continuously improved and integrated across the enterprise. 
              Advanced analytics and metrics are used to measure and enhance performance. 
              The organization is considered a leader in resilience practices within its industry.
            </p>
          </div>
          
          <div style={{marginBottom: '25px'}}>
            <h3 style={{color: '#243444', fontSize: '18px', marginBottom: '10px'}}>Compliant (75-90%)</h3>
            <p style={{color: '#4C5454', lineHeight: '1.6'}}>
              The organization has comprehensive resilience processes that meet or exceed industry standards and regulatory requirements. 
              Processes are well-documented, regularly tested, and continuously monitored. 
              Staff are well-trained and resources are adequately allocated to support resilience activities.
            </p>
          </div>
          
          <div style={{marginBottom: '25px'}}>
            <h3 style={{color: '#243444', fontSize: '18px', marginBottom: '10px'}}>Managed (50-74%)</h3>
            <p style={{color: '#4C5454', lineHeight: '1.6'}}>
              The organization has established resilience processes, but they may not be fully integrated or consistently applied. 
              Some training and resources are available. 
              Basic metrics are in place to measure effectiveness, but improvement opportunities exist.
            </p>
          </div>
          
          <div style={{marginBottom: '25px'}}>
            <h3 style={{color: '#243444', fontSize: '18px', marginBottom: '10px'}}>Defined (20-49%)</h3>
            <p style={{color: '#4C5454', lineHeight: '1.6'}}>
              The organization has defined some resilience processes, but implementation is inconsistent. 
              Processes may be documented but not fully operationalized. 
              Limited training and resources are allocated to resilience activities.
            </p>
          </div>
          
          <div style={{marginBottom: '25px'}}>
            <h3 style={{color: '#243444', fontSize: '18px', marginBottom: '10px'}}>Initial (1-19%)</h3>
            <p style={{color: '#4C5454', lineHeight: '1.6'}}>
              The organization has minimal resilience processes in place. 
              Approaches are largely reactive and ad-hoc. 
              Little to no formal documentation, training, or resource allocation exists for resilience activities.
            </p>
          </div>
          
          <div>
            <h3 style={{color: '#243444', fontSize: '18px', marginBottom: '10px'}}>None (0%)</h3>
            <p style={{color: '#4C5454', lineHeight: '1.6'}}>
              The organization has no established resilience processes or framework. 
              No formal planning, documentation, or resources are allocated to resilience activities.
            </p>
          </div>
        </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default Dashboard;
